import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import GrossRentsService from '../../../services/grossRentsService';
import { promptToaster } from '../../App/actions';
import useIntl from '../../../hooks/useIntl';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import { extractErrorMessage } from '../../../utils/errors';
import tabMessages from './messages';

const grossRentsService = new GrossRentsService();

const STALE_TIME_MS = 60_000;

const useGrossRentChanges = () => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const queryKey = ['grossRentChanges', organizationId, propertyId];
  const errorToastShownRef = useRef(false);

  useEffect(() => {
    errorToastShownRef.current = false;
  }, [organizationId, propertyId]);

  const queryOptions = useMemo(
    () => ({
      enabled: Boolean(organizationId && propertyId),
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME_MS,
      onSuccess: () => {
        errorToastShownRef.current = false;
      },
      onError: (error) => {
        if (errorToastShownRef.current) {
          return;
        }
        errorToastShownRef.current = true;
        promptToaster({
          type: 'error',
          title: intl.formatMessage(tabMessages.errorLoading),
          message: extractErrorMessage(error),
        });
      },
    }),
    [organizationId, propertyId, intl],
  );

  const { data, isLoading, isSuccess, isError, isFetching, refetch } = useQuery(
    queryKey,
    ({ signal }) =>
      grossRentsService.getGrossRentChanges(organizationId, propertyId, {
        signal,
      }),
    queryOptions,
  );

  const grossRentChanges = data ?? [];
  const isRetrying = isFetching && !isLoading;

  return {
    grossRentChanges,
    isLoading,
    isSuccess,
    isError,
    isFetching,
    isRetrying,
    refetch,
  };
};

export default useGrossRentChanges;
